import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const StorySection = () => {
  const sectionRef = useRef(null);
  const slide1Ref = useRef(null);
  const slide2Ref = useRef(null);
  const slide3Ref = useRef(null);
  const slide4Ref = useRef(null);
  const slide5Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Set initial states
      gsap.set(slide1Ref.current, { opacity: 1, y: 0 });
      gsap.set(slide2Ref.current, { opacity: 0, y: 50 });
      gsap.set(slide3Ref.current, { opacity: 0, y: 50 });
      gsap.set(slide4Ref.current, { opacity: 0, y: 50 });
      gsap.set(slide5Ref.current, { opacity: 0, y: 50 });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=350%',
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          snap: {
            snapTo: [0, 0.2, 0.4, 0.6, 0.8, 1], // Snap points for each of the 5 slides
            duration: { min: 0.1, max: 0.3 },
            delay: 0,
            ease: "power3.out",
            inertia: false,
          },
        },
      });

      // Create a clear timeline with distinct phases
      timeline
        // Phase 1: Show slide 1
        .to({}, { duration: 1 })

        // Transition to slide 2
        .to(slide1Ref.current, { opacity: 0, y: -50, duration: 0.5 }, '>')
        .to(slide2Ref.current, { opacity: 1, y: 0, duration: 0.5 }, '<')

        // Phase 2: Show slide 2
        .to({}, { duration: 1 })

        // Transition to slide 3
        .to(slide2Ref.current, { opacity: 0, y: -50, duration: 0.5 }, '>')
        .to(slide3Ref.current, { opacity: 1, y: 0, duration: 0.5 }, '<')

        // Phase 3: Show slide 3
        .to({}, { duration: 1 })

        // Transition to slide 4
        .to(slide3Ref.current, { opacity: 0, y: -50, duration: 0.5 }, '>')
        .to(slide4Ref.current, { opacity: 1, y: 0, duration: 0.5 }, '<')

        // Phase 4: Show slide 4
        .to({}, { duration: 1 })

        // Transition to slide 5 (quote)
        .to(slide4Ref.current, { opacity: 0, y: -50, duration: 0.5 }, '>')
        .to(slide5Ref.current, { opacity: 1, y: 0, duration: 0.5 }, '<')

        // Phase 5: Show slide 5
        .to({}, { duration: 1 });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: 'var(--atelier-cream)' }}
    >
      {/* Slide 1: 1970 - De Oorsprong */}
      <div
        ref={slide1Ref}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="max-w-4xl text-center">
          <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
            1970
          </p>
          <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-8" />
          <h2 className="text-5xl md:text-7xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
            De Eerste Smaak
          </h2>
          <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            In een bescheiden fabriek in Roosendaal ontstaat iets bijzonders.
            De naam Penotti — geïnspireerd op het Italiaanse 'nocciole' voor hazelnoten.
          </p>
          <p className="text-2xl md:text-3xl mt-12 font-light" style={{ color: 'var(--atelier-black)' }}>
            Eén smaak. Eén missie. Perfectie in elke pot.
          </p>
        </div>
      </div>

      {/* Slide 2: 1979 - De Doorbraak */}
      <div
        ref={slide2Ref}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="max-w-4xl text-center">
          <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
            1979
          </p>
          <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-8" />
          <h2 className="text-5xl md:text-7xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
            Twee kleuren. Één obsessie.
          </h2>
          <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            Een revolutionair idee: twee smaken, één pot. Wat begon als experiment werd legendarisch.
            De iconische glazen pot ziet het licht.
          </p>
          <p className="text-2xl md:text-3xl mt-12 font-light italic" style={{ color: 'var(--atelier-gold)' }}>
            "Durf anders te zijn."
          </p>
        </div>
      </div>

      {/* Slide 3: 2011 - De Bewuste Keuze */}
      <div
        ref={slide3Ref}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="max-w-4xl text-center">
          <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
            2011
          </p>
          <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-8" />
          <h2 className="text-5xl md:text-7xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
            Kwaliteit begint bij de bron
          </h2>
          <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            Penotti kiest voor duurzaamheid. Rainforest Alliance gecertificeerde cacao.
            Niet omdat het moest, maar omdat het kon.
          </p>
          <p className="text-2xl md:text-3xl mt-12 font-light" style={{ color: 'var(--atelier-black)' }}>
            Vijf decennia aan lessen. De vraag ontstaat: wat als we alles wat we geleerd hebben bundelen?
          </p>
        </div>
      </div>

      {/* Slide 4: 2026 - Penotti Atelier */}
      <div
        ref={slide4Ref}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="max-w-4xl text-center">
          <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
            2026
          </p>
          <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-8" />
          <h2 className="text-5xl md:text-7xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
            Penotti Atelier
          </h2>
          <p className="text-xl md:text-2xl leading-relaxed mb-6" style={{ color: 'var(--atelier-grey)' }}>
            Vijf decennia vakmanschap. Vijf jaar ontwikkeling. Eén meesterwerk.
          </p>
          <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            Piemonte hazelnoten. Madagascar vanille. Premium ingrediënten. Ambachtelijke bereiding.
          </p>
          <p className="text-3xl md:text-4xl mt-12 font-light italic" style={{ color: 'var(--atelier-gold)' }}>
            Vakmanschap, heruitgevonden.
          </p>
        </div>
      </div>

      {/* Slide 5: The Quote - Prominent */}
      <div
        ref={slide5Ref}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="max-w-5xl text-center">
          <div className="mb-16">
            <div className="text-8xl md:text-9xl font-light mb-4" style={{ color: 'var(--atelier-gold)', opacity: 0.2 }}>"</div>
          </div>
          <blockquote>
            <p className="text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-12" style={{ color: 'var(--atelier-black)' }}>
              Elk meesterwerk begint met het beheersen van de basis.
            </p>
            <p className="text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-16" style={{ color: 'var(--atelier-black)' }}>
              Wij hebben vijftig jaar geoefend.
            </p>
            <footer className="text-xl md:text-2xl tracking-[0.2em] uppercase" style={{ color: 'var(--atelier-gold)' }}>
              — Penotti Atelier
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default StorySection;